import { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { io } from "socket.io-client";
import { PublicRequest } from "../Requests/Requests";
import Loader from "../Components/Loader";
import DownloadApp from "../Components/DownloadApp";
import PetDetailsSkeleton from "../Components/PetDetailsSkeleton";
import moment from "moment/moment";

// Translation data
const translations = {
  en: {
    title: "Hello, I have lost my family.",
    content: "Please, can you accept the location request? I told my family I'm fine. :(",
    thankYouTitle: "We've Notified the Owner",
    thankYouContent: "Thanks for sending your location to my family! Now, if you want to help me more, you can send your contact information to my family. Click 'Next.'",
    contactTitle: "Woow Let My Owner Know you",
    contactContent: "Fill these fields with your mobile number and name.",
    viewLocation: "View Location",
    sendLocation: "Send Location",
    close: "Close",
    next: "Next",
    send: "Send",
    sex: "Sex",
    age: "Age",
    weight: "Weight",
    owner: "Owner",
    yourName: "Your Name",
    yourPhone: "Your Phone Number",
    petInfoMissing: "Please update pet information from the PetIO app",
    downloadApp: "Download app 👇",
    fillAllFields: "Please fill in all fields",
    failedToFetch: "Failed to fetch pet information",
    failedToSend: "Failed to send information",
    geolocationNotSupported: "Geolocation is not supported by your browser"
  },
  ar: {
    title: "مرحباً، لقد فقدت عائلتي.",
    content: "من فضلك، هل يمكنك قبول طلب الموقع؟ أخبر عائلتي أنني بخير. :(",
    thankYouTitle: "لقد تم إشعار المالك",
    thankYouContent: "شكراً لإرسال موقعك لعائلتي! الآن، إذا كنت تريد مساعدتي أكثر، يمكنك إرسال معلومات الاتصال الخاصة بك لعائلتي. انقر على 'التالي'.",
    contactTitle: "رائع! دع مالكي يعرفك",
    contactContent: "املأ هذه الحقول برقم هاتفك واسمك.",
    viewLocation: "عرض الموقع",
    sendLocation: "إرسال الموقع",
    close: "إغلاق",
    next: "التالي",
    send: "إرسال",
    sex: "الجنس",
    age: "العمر",
    weight: "الوزن",
    owner: "المالك",
    yourName: "اسمك",
    yourPhone: "رقم هاتفك",
    petInfoMissing: "يرجى تحديث معلومات الحيوان الأليف من تطبيق PetIO",
    downloadApp: "تحميل التطبيق 👇",
    fillAllFields: "يرجى ملء جميع الحقول",
    failedToFetch: "فشل في جلب معلومات الحيوان الأليف",
    failedToSend: "فشل في إرسال المعلومات",
    geolocationNotSupported: "الموقع الجغرافي غير مدعوم في متصفحك"
  },
  ckb: {
    title: "سڵاو، من خێزانەکەمم لە دەست داوە.",
    content: "تکایە، دەتوانیت داوای شوێن قبوڵ بکەیت؟ بە خێزانەکەم گوتم باشم. :(",
    thankYouTitle: "خاوەنەکەمان ئاگادار کردووە",
    thankYouContent: "سوپاس بۆ ناردنی شوێنەکەت بۆ خێزانەکەم! ئێستا، ئەگەر دەتەوێت زیاتر یارمەتیم بدەیت، دەتوانیت زانیارییەکانی پەیوەندیکردنت بۆ خێزانەکەم بنێریت. کلیک لە 'دواتر' بکە.",
    contactTitle: "زۆر باشە! با خاوەنەکەم بتناسێت",
    contactContent: "ئەم خانانە بە ژمارە مۆبایل و ناوەکەت پڕ بکەرەوە.",
    viewLocation: "بینینی شوێن",
    sendLocation: "ناردنی شوێن",
    close: "داخستن",
    next: "دواتر",
    send: "ناردن",
    sex: "ڕەگەز",
    age: "تەمەن",
    weight: "کێش",
    owner: "خاوەن",
    yourName: "ناوت",
    yourPhone: "ژمارە تەلەفۆنەکەت",
    petInfoMissing: "تکایە زانیارییەکانی ئاژەڵ لە ئەپی PetIO نوێ بکەرەوە",
    downloadApp: "داگرتنی ئەپ 👇",
    fillAllFields: "تکایە هەموو خانەکان پڕ بکەرەوە",
    failedToFetch: "شکستی هێنانی زانیارییەکانی ئاژەڵ",
    failedToSend: "شکستی ناردنی زانیارییەکان",
    geolocationNotSupported: "شوێنی جوگرافی لە وێبگەڕەکەتدا پشتگیری ناکرێت"
  }
};

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  direction: ${props => props.isRTL ? 'rtl' : 'ltr'};
`;

const LanguageSelector = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 100;
  display: flex;
  gap: 8px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
`;

const LanguageButton = styled(motion.button)`
  padding: 8px 12px;
  border: none;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.active ? '#ec4899' : 'transparent'};
  color: ${props => props.active ? 'white' : '#6b7280'};

  &:hover {
    background: ${props => props.active ? '#db2777' : '#f3f4f6'};
  }
`;

const ImageContainer = styled(motion.div)`
  width: 100%;
  height: 300px;
  position: relative;
  overflow: hidden;
`;

const PetImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ContentCard = styled(motion.div)`
  background: white;
  margin: -40px 16px 0;
  border-radius: 20px;
  position: relative;
  z-index: 10;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  padding: 24px;
`;

const Header = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  flex-direction: ${props => props.isRTL ? 'row-reverse' : 'row'};
`;

const PetName = styled(motion.h2)`
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 4px 0;
`;

const Location = styled(motion.p)`
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  font-size: 14px;
  flex-direction: ${props => props.isRTL ? 'row-reverse' : 'row'};
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Button = styled(motion.button)`
  padding: ${(props) => (props.size === "small" ? "8px 12px" : "10px 16px")};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  border: none;

  ${(props) =>
    props.variant === "outline"
      ? `
      background: transparent;
      border: 1px solid #e5e7eb;
      color: #374151;
      &:hover {
        background: #f3f4f6;
      }
    `
      : `
      background: #ec4899;
      color: white;
      &:hover {
        background: #db2777;
      }
    `}
`;

const StatsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  color: white;
`;

const StatValue = styled(motion.p)`
  font-size: 18px;
  font-weight: bold;
  margin: 0 0 4px 0;
`;

const StatLabel = styled(motion.p)`
  font-size: 14px;
  opacity: 0.8;
  margin: 0;
`;

const OwnerCard = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-radius: 12px;
  margin-bottom: 24px;
  flex-direction: ${props => props.isRTL ? 'row-reverse' : 'row'};
`;

const OwnerInfo = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-direction: ${props => props.isRTL ? 'row-reverse' : 'row'};
`;

const OwnerImage = styled(motion.img)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const OwnerName = styled(motion.p)`
  font-weight: 600;
  margin: 0 0 4px 0;
`;

const OwnerRole = styled(motion.p)`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const ContactButtons = styled(motion.div)`
  display: flex;
  gap: 16px;
`;

const IconButton = styled(motion.a)`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.color};
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
  }
`;

const Bio = styled(motion.p)`
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
`;

const Modal = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 50;
`;

const ModalCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  overflow: hidden;
`;

const ModalHeader = styled(motion.div)`
  padding: 24px;
  text-align: center;
`;

const ModalTitle = styled(motion.h3)`
  font-size: 20px;
  font-weight: bold;
  margin: 16px 0 0 0;
`;

const ModalContent = styled(motion.div)`
  padding: 0 24px 24px;
`;

const ModalText = styled(motion.p)`
  text-align: center;
  margin-bottom: 24px;
  color: #4b5563;
  line-height: 1.6;
`;

const Input = styled(motion.input)`
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ec4899;
    box-shadow: 0 0 0 2px rgba(236, 72, 153, 0.1);
  }
`;

const ModalButtons = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" }
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: 0.2 }
  }
};

const imageVariants = {
  hidden: { scale: 1.2, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

const PetDetails = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isModalVisible, setModalVisible] = useState(true);
  const [info, setInfo] = useState(null);
  const [locationSent, setLocationSent] = useState(false);
  const [founderInfo, setFounderInfo] = useState({});
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);
  const [showInformationForm, setShowInformationForm] = useState(false);
  const [scanId, setScanId] = useState("");
  const ioRef = useRef();
  const params = useParams();
  const age = info?.birthDay ? moment(info.birthDay).fromNow() : "N/A";


  const t = translations[currentLanguage];
  const isRTL = currentLanguage === 'ar' || currentLanguage === 'ckb';

  const [flowData, setFlowData] = useState({
    title: t.title,
    content: t.content,
    stage: 1,
  });

  // Update flow data when language changes
  useEffect(() => {
    switch (flowData.stage) {
      case 1:
        setFlowData(prev => ({
          ...prev,
          title: t.title,
          content: t.content
        }));
        break;
      case 2:
        setFlowData(prev => ({
          ...prev,
          title: t.thankYouTitle,
          content: t.thankYouContent
        }));
        break;
      case 3:
        setFlowData(prev => ({
          ...prev,
          title: t.contactTitle,
          content: t.contactContent
        }));
        break;
    }
  }, [currentLanguage, t]);

  const pushNotificationSend = useCallback(
    (data = {}) => {
      ioRef.current = io("https://petioapi-production.up.railway.app/");
      ioRef.current?.emit("newScan", { id: params?.id, data });
    },
    [params?.id]
  );

  useEffect(() => {
    const fetchPetInfo = async () => {
      try {
        const response = await PublicRequest.get(`/auth/getuser/${params.id}`);
        setInfo(response.data);
      } catch (err) {
        setError(t.failedToFetch);
      }
    };
    fetchPetInfo();
  }, [params.id, t.failedToFetch]);

  useEffect(() => {
    getLocation();
  }, []);

  const getLocation = () => {
    if (!navigator.geolocation) {
      setError(t.geolocationNotSupported);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await PublicRequest.post("/newScan", {
            location: {
              longitude: position.coords.longitude,
              latitude: position.coords.latitude,
            },
            petOwnerId: params.id,
          });

          setScanId(response.data);
          stageHandler(1);
          setLocationSent(true);
          pushNotificationSend();
          setCoords(position.coords);
        } catch (err) {
          setError(t.failedToSend);
        }
      },
      (err) => setError(err.message)
    );
  };

  const handelInfoChange = (f, v) => {
    setFounderInfo({ ...founderInfo, [f]: v });
  };

  const stageHandler = (stageLevel) => {
    switch (stageLevel) {
      case 1:
        setFlowData({
          content: t.thankYouContent,
          title: t.thankYouTitle,
          stage: 2,
        });
        break;
      case 2:
        setFlowData({
          content: t.contactContent,
          title: t.contactTitle,
          stage: 3,
        });
        break;
      default:
        break;
    }
  };

  const petImageAndStage = () => {
    switch (true) {
      case flowData.stage === 1 && info?.petInfo?.pet === "dog":
        return "/images/lostSad.png";
      case flowData.stage >= 2 && info?.petInfo?.pet === "dog":
        return "/images/thanksDog.png";
      case flowData.stage === 1 && info?.petInfo?.pet === "cat":
        return "/images/lostCat.png";
      case flowData.stage >= 2 && info?.petInfo?.pet === "cat":
        return "/images/ThanksCat.png";
      default:
        return "no type";
    }
  };

  const showFormAndNextStage = () => {
    setShowInformationForm(true);
    stageHandler(2);
  };

  const sendLocationWithInfo = async () => {
    if (!founderInfo.founderName || !founderInfo.founderNumber) {
      setError(t.fillAllFields);
      return;
    }

    try {
      const response = await PublicRequest.post("/newScan", {
        location: { latitude: coords.latitude, longitude: coords.longitude },
        petOwnerId: params.id,
        ...founderInfo,
      });
      setScanId(response.data);
      setModalVisible(false);
      setLocationSent(true);
      pushNotificationSend({ ...founderInfo });
    } catch (err) {
      setError(t.failedToSend);
    }
  };

  if (!info) {
    return (
      <PetDetailsSkeleton />
    );
  }

  if (!info?.petInfo || !info.hasTag) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          padding: "16px",
        }}
      >
        <motion.img
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 20 }}
          src="/images/catmad.jpeg"
          alt="Cat"
          style={{
            width: "256px",
            height: "256px",
            objectFit: "cover",
            borderRadius: "50%",
            marginBottom: "24px",
          }}
        />
        <motion.h3
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{ textAlign: "center", marginBottom: "16px" }}
        >
          {t.petInfoMissing}
        </motion.h3>
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          style={{ marginBottom: "16px" }}
        >
          {t.downloadApp}
        </motion.p>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <DownloadApp />
        </motion.div>
      </motion.div>
    );
  }

  return (
    <Container isRTL={isRTL}>
      <LanguageSelector
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        {['en', 'ar', 'ckb'].map((lang) => (
          <LanguageButton
            key={lang}
            active={currentLanguage === lang}
            onClick={() => setCurrentLanguage(lang)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {lang === 'en' ? 'EN' : lang === 'ar' ? 'عربي' : 'کوردی'}
          </LanguageButton>
        ))}
      </LanguageSelector>

      <AnimatePresence>
        {isModalVisible && (
          <Modal
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <ModalCard>
              <ModalHeader>
                <motion.img
                  src={petImageAndStage()}
                  alt="Pet"
                  style={{ width: "100%" }}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                />
                <ModalTitle
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  {flowData.title}
                </ModalTitle>
              </ModalHeader>
              <ModalContent>
                <ModalText
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  {flowData.content}
                </ModalText>

                <AnimatePresence>
                  {showInformationForm && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        overflow: "hidden"
                      }}
                    >
                      <Input
                        name="founderName"
                        placeholder={t.yourName}
                        onChange={(e) =>
                          handelInfoChange(e.target.name, e.target.value)
                        }
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                      />
                      <Input
                        name="founderNumber"
                        type="tel"
                        placeholder={t.yourPhone}
                        onChange={(e) =>
                          handelInfoChange(e.target.name, e.target.value)
                        }
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <ModalButtons
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <Button
                    variant="outline"
                    onClick={() => setModalVisible(false)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t.close}
                  </Button>
                  <Button
                    onClick={() => {
                      if (flowData.stage === 1) getLocation();
                      else if (flowData.stage === 2) showFormAndNextStage();
                      else sendLocationWithInfo();
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {flowData.stage === 1
                      ? t.sendLocation
                      : flowData.stage === 2
                        ? t.next
                        : t.send}
                  </Button>
                </ModalButtons>
              </ModalContent>
            </ModalCard>
          </Modal>
        )}
      </AnimatePresence>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <ImageContainer variants={itemVariants}>
          <PetImage
            src={info.petInfo.image}
            alt={info.petInfo.name}
            variants={imageVariants}
          />
        </ImageContainer>

        <ContentCard variants={itemVariants}>
          <Header variants={itemVariants} isRTL={isRTL}>
            <div>
              <PetName variants={itemVariants}>{info.petInfo.name}</PetName>
              <Location variants={itemVariants} isRTL={isRTL}>
                <i className="fi fi-rr-marker" /> {info.location}
              </Location>
            </div>
            <ButtonGroup variants={itemVariants}>
              <Button
                variant="outline"
                size="small"
                onClick={() =>
                  window.open(
                    `https://www.google.com/maps?q=${info?.petInfo.daynamicLocation?.latitude},${info?.petInfo.daynamicLocation?.longitude}`,
                    "_blank"
                  )
                }
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <i className="fi fi-rr-marker" /> {t.viewLocation}
              </Button>
              <Button
                size="small"
                onClick={() => setModalVisible(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <i className="fi fi-rr-paper-plane" /> {t.sendLocation}
              </Button>
            </ButtonGroup>
          </Header>

          <StatsGrid variants={itemVariants}>
            {[
              { value: info.petInfo.sex, label: t.sex },
              { value: age?.split(" ")[0], label: t.age },
              { value: info.petInfo.weight, label: t.weight }
            ].map((stat, index) => (
              <StatCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -2 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <StatValue variants={itemVariants}>{stat.value}</StatValue>
                <StatLabel variants={itemVariants}>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>

          <OwnerCard variants={itemVariants} isRTL={isRTL}>
            <OwnerInfo variants={itemVariants} isRTL={isRTL}>
              <OwnerImage
                src={info.profilePic}
                alt={info.name}
                variants={itemVariants}
                whileHover={{ scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
              <div>
                <OwnerName variants={itemVariants}>{info.name}</OwnerName>
                <OwnerRole variants={itemVariants}>{t.owner}</OwnerRole>
              </div>
            </OwnerInfo>
            <ContactButtons variants={itemVariants}>
              <IconButton
                href={`tel:${info.phone}`}
                color="#ec4899"
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <i className="fi fi-br-phone-flip" style={{ fontSize: "20px" }} />
              </IconButton>
              <IconButton
                href={`sms:${info.phone}`}
                color="#60a5fa"
                whileHover={{ scale: 1.1, rotate: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <i
                  className="fi fi-rr-comment-dots"
                  style={{ fontSize: "20px" }}
                />
              </IconButton>
            </ContactButtons>
          </OwnerCard>

          <Bio variants={itemVariants}>{info.petInfo.bio}</Bio>
        </ContentCard>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#ef4444',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '12px',
            zIndex: 1000,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
          }}
        >
          {error}
        </motion.div>
      )}
    </Container>
  );
};

export default PetDetails;